// Modul pro zobrazení veřejné nabídky (read-only)
// Logo tiskárny – stejný asset, jaký se používá v hlavičce aplikace.
// `url:` prefix říká Parcelu 2, aby import vrátil URL k souboru (nikoli inline obsah).
import logoUrl from 'url:../images/logo-icon.svg';
import {
  buildFinishingLines,
  stripFinishingFromName,
  OFFER_FOOTER_DISCLAIMER
} from './shared/offer-item-format';
import { icon } from './icons.js';

export class PublicOfferManager {
  constructor() {
    this.currentOffer = null;
    this.token = null;
  }

  /** Normalizuje customerData (string i objekt z MCP/AI) na text. */
  formatCustomerData(data) {
    if (!data) return '';
    if (typeof data === 'string') return data;
    if (typeof data === 'object') {
      const d = data;
      const lines = [];
      if (d.name) lines.push(d.name);
      if (d.contactPerson && d.contactPerson !== d.name) lines.push(d.contactPerson);
      if (d.address) lines.push(d.address);
      const cityLine = [d.zip, d.city].filter(Boolean).join(' ');
      if (cityLine) lines.push(cityLine);
      if (d.country && d.country !== 'Česká republika') lines.push(d.country);
      const contact = [d.phone, d.email].filter(Boolean).join(', ');
      if (contact) lines.push(contact);
      const taxLine = [d.ico ? `IČ: ${d.ico}` : '', d.dic ? `DIČ: ${d.dic}` : ''].filter(Boolean).join(', ');
      if (taxLine) { lines.push(''); lines.push(taxLine); }
      return lines.join('\n');
    }
    return String(data);
  }

  async loadOfferByToken(token) {
    this.token = token;
    
    try {
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
        ? 'http://localhost:1234/api' 
        : '/api';
      
      const response = await fetch(`${API_BASE_URL}/public/offer/${token}`);

      if (!response.ok) {
        if (response.status === 404) {
          this.showError('Nabídka nebyla nalezena nebo odkaz není platný');
        } else {
          this.showError('Chyba při načítání nabídky');
        }
        return;
      }

      const result = await response.json();
      
      if (result.success && result.data && result.data.offer) {
        this.currentOffer = result.data.offer;
        this.renderPublicOffer();
      } else {
        this.showError('Neplatná odpověď od serveru');
      }
    } catch (error) {
      console.error('Chyba při načítání veřejné nabídky:', error);
      this.showError('Chyba při načítání nabídky');
    }
  }

  showError(message) {
    const container = document.getElementById('public-offer-content');
    if (!container) return;

    container.innerHTML = `
      <div class="text-center p-8">
        ${icon('exclamation-triangle', 'mx-auto h-12 w-12 text-red-400')}
        <h3 class="mt-2 text-sm font-medium text-gray-900">${message}</h3>
      </div>
    `;
  }

  renderPublicOffer() {
    if (!this.currentOffer) return;

    const container = document.getElementById('public-offer-content');
    if (!container) return;

    const date = new Date(this.currentOffer.createdAt);
    const formattedDate = date.toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Vypočítat celkovou cenu a cenu s DPH
    const totalPrice = this.currentOffer.totalPrice || 0;
    const vatRate = 0.21; // 21% DPH
    const totalPriceWithVat = totalPrice * (1 + vatRate);

    // Získat data odběratele (může být string i objekt z MCP/AI nabídky)
    const customerData = this.formatCustomerData(this.currentOffer.customerData);

    const html = `
      <div class="max-w-4xl mx-auto">
        <!-- Header s logem a číslem nabídky -->
        <header class="flex flex-col sm:flex-row sm:items-start pb-6 border-b border-black mb-6">
          <div class="w-full sm:w-1/2 mb-4 sm:mb-0">
            <img src="${logoUrl}" alt="Tiskárna Tria, s.r.o." class="h-12 w-auto">
          </div>
          <div class="w-full sm:w-1/2 sm:text-right">
            <h1 class="text-xl sm:text-2xl font-semibold mb-2">Cenová nabídka č. ${this.currentOffer.id}</h1>
            <div class="text-sm text-gray-500">Ze dne: ${formattedDate}</div>
            <div class="text-sm text-gray-500">Vystavil: ${this.currentOffer.createdBy || 'Systém'}</div>
          </div>
        </header>

        <!-- Odběratel a Dodavatel -->
        <section class="flex flex-col sm:flex-row gap-6 mb-6 pt-2">
          <div class="w-full sm:w-1/2">
            <p class="offer-party-label">Odběratel</p>
            <div class="text-sm whitespace-pre-line">${customerData || 'Neuvedeno'}</div>
          </div>

          <div class="w-full sm:w-1/2">
            <p class="offer-party-label">Dodavatel</p>
            <div class="text-sm">
              <p>Tiskárna Tria s.r.o.<br>
              Olbramovice Ves 31,<br>
              259 01 Olbramovice</p>
              <p class="mt-2">IČO: 49546112<br>
              DIČ: CZ49546112</p>
              <p class="mt-2">Email: tiskarna@tria.cz<br>
              Web: www.tria.cz</p>
            </div>
          </div>
        </section>

        <!-- Tabulka položek - responzivní s horizontálním scrollem na mobilu -->
        <div class="overflow-x-auto mb-6">
          <table class="w-full border-collapse min-w-full">
            <thead>
              <tr class="border-b border-black">
                <th class="py-2 px-1 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Označení položky</th>
                <th class="py-2 px-1 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap">Počet kusů</th>
                <th class="py-2 px-1 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap">Cena za kus</th>
                <th class="py-2 px-1 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Cena</th>
                <th class="py-2 px-1 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">% DPH</th>
                <th class="py-2 px-1 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap">Cena s DPH</th>
              </tr>
            </thead>
            <tbody>
              ${this.currentOffer.items.map(item => this.renderOfferItemRow(item)).join('')}
            </tbody>
          </table>
        </div>

        <!-- Celkem s DPH – box s rozpisem (stejně jako PDF) -->
        <section class="flex justify-end mb-6">
          <div class="min-w-[260px] text-sm">
            <div class="flex justify-between py-1">
              <span class="text-gray-500">Cena celkem (bez DPH)</span>
              <span class="tabular-nums">${totalPrice.toLocaleString('cs-CZ')} Kč</span>
            </div>
            <div class="flex justify-between py-1">
              <span class="text-gray-500">DPH ${(vatRate * 100).toFixed(0)} %</span>
              <span class="tabular-nums">${(totalPrice * vatRate).toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</span>
            </div>
            <div class="flex justify-between pt-2 mt-1 border-t border-black text-base font-bold">
              <span>Celkem s DPH</span>
              <span class="tabular-nums">${totalPriceWithVat.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</span>
            </div>
          </div>
        </section>

        <div class="public-offer-disclaimer mt-10 pt-6 border-t border-gray-300 text-xs text-gray-500 leading-relaxed text-center px-2">
          ${OFFER_FOOTER_DISCLAIMER}
        </div>
      </div>
    `;
    
    container.innerHTML = html;
  }

  renderOfferItemRow(item) {
    const vatRate = 0.21; // 21% DPH
    const priceWithVat = item.price * (1 + vatRate);
    const quantity = Number(item.formData?.pocetKusu) || 1;
    const unitPrice = item.pricePerPiece || (quantity > 0 ? item.price / quantity : 0);
    const displayName = stripFinishingFromName(item.name || '');
    const lines = item.formData ? buildFinishingLines(item.formData) : [];

    return `
      <tr class="${lines.length ? '' : 'border-b border-gray-200'}">
        <td class="py-2 px-1 text-sm align-top">
          <div class="font-medium">${displayName}</div>
          ${item.notes && item.notes.trim() ? `<div class="text-xs text-gray-500 mt-0.5">${item.notes}</div>` : ''}
        </td>
        <td class="py-2 px-1 text-sm text-right whitespace-nowrap align-top tabular-nums">${quantity.toLocaleString('cs-CZ')} ks</td>
        <td class="py-2 px-1 text-sm text-right whitespace-nowrap align-top tabular-nums">${unitPrice.toLocaleString('cs-CZ', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} Kč</td>
        <td class="py-2 px-1 text-sm text-right whitespace-nowrap align-top tabular-nums">${item.price.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</td>
        <td class="py-2 px-1 text-sm text-right whitespace-nowrap align-top tabular-nums">${(vatRate * 100).toFixed(0)} %</td>
        <td class="py-2 px-1 text-sm text-right whitespace-nowrap align-top tabular-nums">${priceWithVat.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</td>
      </tr>
      ${lines.length ? `
        <tr class="border-b border-gray-200">
          <td colspan="6" class="pt-0 pb-3 px-1 text-xs text-gray-500 leading-relaxed">
            ${lines.join('<br>')}
          </td>
        </tr>
      ` : ''}
    `;
  }

  // Zpětně-kompatibilní wrapper: vrací HTML se <br>. Skutečná logika je v
  // shared/offer-item-format.js, níže uvedené helpery jsou už jen dead code.
  formatItemSpecification(formData) {
    return buildFinishingLines(formData || {}).join('<br>');
  }

  _legacyFormatItemSpecification(formData) {
    let spec = [];

    // Digitální speciální barvy
    if (formData.barvyDigital && formData.barvyDigital !== '0') {
      const digitalText = this.getDigitalColorName(formData.barvyDigital);
      if (digitalText) {
        spec.push(`Digitální speciální barva: ${digitalText}`);
      }
    }

    // Lakování
    if (formData.lakovani && formData.lakovani !== '0') {
      const lakText = this.getLakText(formData.lakovani);
      if (lakText) {
        spec.push(`Lakování: ${lakText}`);
      }
    }

    // Laminace
    if (formData.laminace && formData.laminace !== '0') {
      const laminaceText = this.getLaminaceText(formData.laminace);
      if (laminaceText) {
        spec.push(`Laminace: ${laminaceText}`);
      }
    }

    // Falcování
    if (formData.falcovani && formData.falcovani !== '0') {
      const falcovaniText = this.getFalcovaniText(formData.falcovani);
      if (falcovaniText) {
        spec.push(`Falcování: ${falcovaniText}`);
      }
    }

    // Bigování
    if (formData.bigovani && formData.bigovani !== '0') {
      const bigovaniText = this.getBigovaniText(formData.bigovani);
      if (bigovaniText) {
        spec.push(`Bigování: ${bigovaniText}`);
      }
    }

    // Výsek
    if (formData.vysek && formData.vysek !== '0') {
      const vysekText = this.getVysekText(formData.vysek);
      const pozicNaVysek = formData.pozicNaVysek || '1';
      if (vysekText) {
        spec.push(`Výsek: ${vysekText} (${pozicNaVysek} pozic)`);
      }
    }

    // Předsek
    if (formData.predsek && formData.predsek !== '0') {
      const predsekText = this.getPredsekText(formData.predsek);
      const pozicNaPredsek = formData.pozicNaPredsek || '1';
      if (predsekText) {
        spec.push(`Předsek: ${predsekText} (${pozicNaPredsek} pozic)`);
      }
    }

    // Ražba
    if (formData.razba && formData.razba !== '0') {
      const razbaText = this.getRazbaText(formData.razba);
      const pozicNaRazbu = formData.pozicNaRazbu || '1';
      if (razbaText) {
        spec.push(`Ražba: ${razbaText} (${pozicNaRazbu} pozic)`);
      }
    }

    // Vrtání
    if (formData.vrtani && formData.vrtani !== '0') {
      const vrtaniText = this.getVrtaniText(formData.vrtani);
      if (vrtaniText) {
        spec.push(`Vrtání: ${vrtaniText}`);
      }
    }

    // Perforace
    if (formData.perforace && formData.perforace !== '0') {
      const pocetPerforaci = parseInt(formData.perforace);
      if (pocetPerforaci > 0) {
        spec.push(`Perforace ${pocetPerforaci}x`);
      }
    }

    // Lepení
    if (formData.lepeni && formData.lepeni !== '0') {
      const lepeniText = this.getLepeniText(formData.lepeni);
      if (lepeniText) {
        spec.push(lepeniText);
      }
    }

    // Vazba
    if (formData.vazba && formData.vazba !== '0') {
      const vazbaText = this.getVazbaText(formData.vazba);
      if (vazbaText) {
        spec.push(vazbaText);
      }
    }

    return spec.join('<br>');
  }

  // Pomocné funkce pro převod hodnot na texty (shodné s offer-editor.js)
  getDigitalColorName(typ) {
    const nazvy = {
      'lak_10_50': '1/0 lak (parciální)',
      'lak_11_50': '1/1 lak (parciální)',
      'lak_10_100': '1/0 lak (celá plocha)',
      'lak_11_100': '1/1 lak (celá plocha)',
      'bila_10_50': '1/0 bílá (parciální)',
      'bila_11_50': '1/1 bílá (parciální)',
      'bila_10_100': '1/0 bílá (celá plocha)',
      'bila_11_100': '1/1 bílá (celá plocha)',
      'zlata_10_50': '1/0 zlatá (parciální)',
      'zlata_11_50': '1/1 zlatá (parciální)',
      'zlata_10_100': '1/0 zlatá (celá plocha)',
      'zlata_11_100': '1/1 zlatá (celá plocha)'
    };
    return nazvy[typ] || '';
  }

  getLakText(typ) {
    const nazvy = {
      'parcial10': '1/0 UV lak parciální',
      'parcial11': '1/1 UV lak parciální',
      'plocha10': '1/0 UV lak celá plocha',
      'plocha11': '1/1 UV lak celá plocha',
      'stroj10': '1/0 Stroj lak',
      'stroj11': '1/1 Stroj lak'
    };
    return nazvy[typ] || '';
  }

  getLaminaceText(typ) {
    const nazvy = {
      'lesk10': '1/0 Lamino lesklé',
      'lesk11': '1/1 Lamino lesklé',
      'mat10': '1/0 Lamino matné',
      'mat11': '1/1 Lamino matné',
      'struktura10': '1/0 Lamino nepoškrabatelné',
      'struktura11': '1/1 Lamino nepoškrabatelné',
      'samet10': '1/0 Lamino sametové',
      'samet11': '1/1 Lamino sametové',
      'kapsaA5': '1/1 Laminace kapsová (A5)',
      'kapsaA4': '1/1 Laminace kapsová (A4)',
      'kapsaA3': '1/1 Laminace kapsová (A3)'
    };
    return nazvy[typ] || '';
  }

  getFalcovaniText(typ) {
    const nazvy = {
      'half-fold': 'Na polovinu (1 lom)',
      'cross-fold': 'Křížový lom (2 lomy)',
      'u-fold': 'Do U (2 lomy)',
      'z-fold': 'Do Z (2 lomy)',
      'double-half-fold': 'Na polovinu (2 lomy)',
      'roll-fold': 'Harmonikové s přebalem (3 lomy)',
      'harmonium-fold-3': 'Harmonikové skládání (3 lomy)',
      'harmonium-fold-4': 'Harmonikové skládání (4 lomy)',
      'harmonium-fold-5': 'Harmonikové skládání (5 lomů)',
      'gate-fold': 'Skládání do psaníčka (3 lomy)',
      'double-gate-fold': 'Zavřené psaníčko (3 lomy)'
    };
    return nazvy[typ] || '';
  }

  getBigovaniText(typ) {
    const nazvy = {
      'half-fold': 'Na polovinu (1 big)',
      'cross-fold': 'Do kříže (2 bigy)',
      'u-fold': 'Do U (2 bigy)',
      'z-fold': 'Do Z (2 bigy)',
      'double-half-fold': 'Na polovinu (2 bigy)',
      'roll-fold': 'Harmonikové (3 bigy)',
      'harmonium-fold-3': 'Harmonikové (3 bigy)',
      'harmonium-fold-4': 'Harmonikové (4 bigy)',
      'harmonium-fold-5': 'Harmonikové (5 bigy)',
      'gate-fold': 'Skládání do psaníčka (3 bigy)',
      'double-gate-fold': 'Zavřené psaníčko (3 bigy)'
    };
    return nazvy[typ] || '';
  }

  getVysekText(option) {
    const nazvy = {
      'vlastni': 'Vlastní/půjčené výsekové železo',
      'nove': 'Nové výsekové železo'
    };
    return nazvy[option] || '';
  }

  getPredsekText(option) {
    const nazvy = {
      '1': 'Vlastní/půjčené předsekové železo',
      '2': 'Nové předsekové železo'
    };
    return nazvy[option] || '';
  }

  getRazbaText(option) {
    const nazvy = {
      '1': 'Vlastní/půjčený štoček',
      '2': 'Nový štoček'
    };
    return nazvy[option] || '';
  }

  getVrtaniText(typ) {
    const nazvy = {
      '1dira2mm': '1 díra (2 mm)',
      '1dira4mm': '1 díra (4 mm)',
      '1dira5mm': '1 díra (5,5 mm)',
      '2diry2mm': '2 díry (2 mm)',
      '2diry4mm': '2 díry (4 mm)',
      '2diry5mm': '2 díry (5,5 mm)',
      'euro2diry2mm': 'Euro blok, 2 díry (2 mm)',
      'euro2diry4mm': 'Euro blok, 2 díry (4 mm)',
      'euro2diry5mm': 'Euro blok, 2 díry (5,5 mm)',
      '3diry2mm': '3 díry (2 mm)',
      '3diry4mm': '3 díry (4 mm)',
      '3diry5mm': '3 díry (5,5 mm)',
      'euro4diry2mm': 'Euro blok, 4 díry (2 mm)',
      'euro4diry4mm': 'Euro blok, 4 díry (4 mm)',
      'euro4diry5mm': 'Euro blok, 4 díry (5,5 mm)'
    };
    return nazvy[typ] || '';
  }

  getLepeniText(typ) {
    const nazvy = {
      '1': 'Lepení krabiček'
    };
    return nazvy[typ] || '';
  }

  getVazbaText(typ) {
    const nazvy = {
      'V1-strojni': 'V1 (2 skoby) strojní',
      'V1-rucni': 'V1 (2 skoby) ručně',
      'blok': 'Lepený blok',
      'blokPrebal': 'Lepený blok s přebalem',
      'V2-PUR': 'V2 lepená (PUR)',
      'V3': 'V3 lepená a šitá',
      'V6': 'V6 leporelo',
      'krouzek-blok': 'Kroužková twin wire',
      'krouzek-kalendar': 'Kroužková s háčkem na pověšení',
      'V4': 'V4 šitá, měkká obálka',
      'V5': 'V5 šitá, tvrdá obálka',
      'V2V8': 'V2V8 – falešná V8',
      'V8': 'V8 šitá, tvrdá obálka'
    };
    return nazvy[typ] || 'Neznámá vazba';
  }
}

// Export pro použití v app.js
window.PublicOfferManager = PublicOfferManager;

